import { useState, useEffect, useMemo } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
    House, Users, UsersThree, ShieldCheck, FilesIcon, ListChecks,
    Gear, SignOut, Buildings, Stack, Lifebuoy, CaretLeft, CaretRight, CreditCard, UserCircle, ChartBar,
    CaretDown, CaretRight as CaretRightSmall, ClockCounterClockwise, Question,
    ArrowsOut, ArrowsIn,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useLayoutPrefs } from "@/contexts/LayoutPrefsContext";

const navItems = [
    { to: "/dashboard", label: "Overview", icon: House, testid: "nav-overview", admin: true },
    { to: "/approvals", label: "Approvals", icon: Lifebuoy, testid: "nav-approvals" },
    { to: "/workflow-history", label: "Workflow history", icon: ClockCounterClockwise, testid: "nav-workflow-history", admin: true },
    { to: "/forms", label: "Forms", icon: FilesIcon, testid: "nav-forms", admin: true },
    { to: "/users", label: "Users", icon: Users, testid: "nav-users", admin: true },
    { to: "/groups", label: "Groups", icon: UsersThree, testid: "nav-groups", admin: true },
    { to: "/roles", label: "Roles", icon: ShieldCheck, testid: "nav-roles", admin: true },
    { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing", admin: true },
    { to: "/audit", label: "Audit Log", icon: ListChecks, testid: "nav-audit", admin: true },
    { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings", admin: true },
    { to: "/help", label: "Help", icon: Question, testid: "nav-help" },
    { to: "/profile", label: "Profile", icon: UserCircle, testid: "nav-profile" },
];

export default function DashboardLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isAdmin = user && (user.role === "platform_admin" || user.role === "org_admin");
    const visibleNavItems = useMemo(() => navItems.filter((it) => !it.admin || isAdmin), [isAdmin]);

    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem("ff-sidebar-collapsed") === "1"; } catch { return false; }
    });
    const [appsOpen, setAppsOpen] = useState(() => {
        try { return localStorage.getItem("ff-apps-tree-open") !== "0"; } catch { return true; }
    });
    const [reportsOpen, setReportsOpen] = useState(() => {
        try { return localStorage.getItem("ff-reports-tree-open") !== "0"; } catch { return true; }
    });
    const [myApps, setMyApps] = useState([]);
    const [myReports, setMyReports] = useState([]);

    useEffect(() => {
        try { localStorage.setItem("ff-sidebar-collapsed", collapsed ? "1" : "0"); }
        catch (e) { console.warn("[sidebar] localStorage blocked:", e?.message); }
    }, [collapsed]);
    useEffect(() => {
        try { localStorage.setItem("ff-apps-tree-open", appsOpen ? "1" : "0"); }
        catch (e) { console.warn("[sidebar] localStorage blocked:", e?.message); }
    }, [appsOpen]);
    useEffect(() => {
        try { localStorage.setItem("ff-reports-tree-open", reportsOpen ? "1" : "0"); }
        catch (e) { console.warn("[sidebar] localStorage blocked:", e?.message); }
    }, [reportsOpen]);

    // Auto-open tree when route enters a form
    useEffect(() => {
        if (location.pathname.startsWith("/forms/") || location.pathname === "/portal") setAppsOpen(true);
    }, [location.pathname]);

    // Load accessible apps for the tree
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/forms/my");
                if (!cancelled) setMyApps(data);
            } catch (err) {
                console.warn("[sidebar] /forms/my failed:", err?.response?.status || err?.message);
                if (!cancelled) setMyApps([]);
            }
            try {
                const { data } = await api.get("/report-variants/mine");
                if (!cancelled) setMyReports(data);
            } catch (err) {
                console.warn("[sidebar] /report-variants/mine failed:", err?.response?.status || err?.message);
                if (!cancelled) setMyReports([]);
            }
        })();
        return () => { cancelled = true; };
        // Reload when user changes or the user navigates into the forms/portal/reports surface area.
    }, [user, location.pathname]);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const activeAppId = (() => {
        const m = /^\/forms\/([^/]+)/.exec(location.pathname);
        return m ? m[1] : null;
    })();
    const isPortalActive = location.pathname === "/portal";

    return (
        <div className="min-h-screen flex bg-background">
            {/* Sidebar */}
            <aside
                className={`${collapsed ? "w-16" : "w-64"} transition-all duration-200 border-r border-border bg-card flex flex-col`}
                data-testid="app-sidebar"
                data-collapsed={collapsed ? "1" : "0"}
            >
                <div className={`${collapsed ? "px-3" : "px-6"} py-5 border-b border-border flex items-center justify-between gap-2`}>
                    {!collapsed ? (
                        <div>
                            <div className="label-overline mb-1">Workspace</div>
                            <div className="text-xl font-display font-bold tracking-tight">FormFlo<span className="text-primary">.</span></div>
                        </div>
                    ) : (
                        <div className="text-xl font-display font-bold tracking-tight">F<span className="text-primary">.</span></div>
                    )}
                    <button
                        onClick={() => setCollapsed(!collapsed)}
                        className="p-1.5 hover:bg-secondary rounded-sm transition-colors flex items-center justify-center"
                        data-testid="sidebar-toggle-btn"
                        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        {collapsed ? <CaretRight size={14} /> : <CaretLeft size={14} />}
                    </button>
                </div>
                {!collapsed && (
                    <div className="px-6 py-4 border-b border-border">
                        <div className="label-overline mb-1">Organization</div>
                        <div className="flex items-center gap-2">
                            <Buildings size={16} weight="duotone" />
                            <div className="text-sm font-medium truncate" data-testid="sidebar-org-name">{user?.org_name || "—"}</div>
                        </div>
                    </div>
                )}
                <nav className={`flex-1 ${collapsed ? "p-2" : "p-3"} space-y-1 overflow-y-auto`}>
                    {/* My Apps tree */}
                    <div className="mb-1" data-testid="my-apps-tree">
                        <div className={`flex items-center ${collapsed ? "justify-center" : ""} rounded-sm overflow-hidden`}>
                            <Link
                                to="/portal"
                                data-testid="nav-portal"
                                title={collapsed ? "My Apps" : undefined}
                                className={`flex-1 flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2 text-sm transition-colors ${
                                    isPortalActive ? "bg-foreground text-background" : "hover:bg-secondary text-foreground/80"
                                }`}
                            >
                                <Stack size={18} weight="duotone" />
                                {!collapsed && <span className="font-medium">My Apps</span>}
                                {!collapsed && myApps.length > 0 && (
                                    <span className={`ml-auto text-[10px] font-mono tabular-nums ${isPortalActive ? "opacity-60" : "text-muted-foreground"}`}>{myApps.length}</span>
                                )}
                            </Link>
                            {!collapsed && myApps.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setAppsOpen(!appsOpen)}
                                    className="px-2 py-2 hover:bg-secondary"
                                    data-testid="apps-tree-toggle"
                                    aria-label={appsOpen ? "Collapse apps" : "Expand apps"}
                                >
                                    {appsOpen ? <CaretDown size={12} /> : <CaretRightSmall size={12} />}
                                </button>
                            )}
                        </div>
                        {!collapsed && appsOpen && myApps.length > 0 && (
                            <ul className="mt-1 ml-3 border-l border-border pl-2 space-y-0.5" data-testid="apps-tree-children">
                                {myApps.map((f) => (
                                    <li key={f.id}>
                                        <Link
                                            to={`/forms/${f.id}/records`}
                                            data-testid={`apps-tree-${f.id}`}
                                            title={f.form_name}
                                            className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors ${
                                                activeAppId === f.id ? "bg-primary/10 text-primary font-medium" : "hover:bg-secondary text-foreground/70"
                                            }`}
                                        >
                                            <FilesIcon size={12} weight="duotone" />
                                            <span className="truncate">{f.form_name}</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Reports tree */}
                    <div className="mb-1" data-testid="my-reports-tree">
                        <div className={`flex items-center ${collapsed ? "justify-center" : ""} rounded-sm overflow-hidden`}>
                            <Link
                                to="/reports"
                                data-testid="nav-reports"
                                title={collapsed ? "Reports" : undefined}
                                className={`flex-1 flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2 text-sm transition-colors ${
                                    location.pathname.startsWith("/reports") ? "bg-foreground text-background" : "hover:bg-secondary text-foreground/80"
                                }`}
                            >
                                <ChartBar size={18} weight="duotone" />
                                {!collapsed && <span className="font-medium">Reports</span>}
                                {!collapsed && myReports.length > 0 && (
                                    <span className={`ml-auto text-[10px] font-mono tabular-nums ${location.pathname.startsWith("/reports") ? "opacity-60" : "text-muted-foreground"}`}>{myReports.length}</span>
                                )}
                            </Link>
                            {!collapsed && myReports.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setReportsOpen(!reportsOpen)}
                                    className="px-2 py-2 hover:bg-secondary"
                                    data-testid="reports-tree-toggle"
                                    aria-label={reportsOpen ? "Collapse reports" : "Expand reports"}
                                >
                                    {reportsOpen ? <CaretDown size={12} /> : <CaretRightSmall size={12} />}
                                </button>
                            )}
                        </div>
                        {!collapsed && reportsOpen && myReports.length > 0 && (
                            <ul className="mt-1 ml-3 border-l border-border pl-2 space-y-0.5" data-testid="reports-tree-children">
                                {myReports.map((r) => (
                                    <li key={r.id}>
                                        <Link
                                            to={`/reports?open=${r.id}`}
                                            data-testid={`reports-tree-${r.id}`}
                                            title={r.name}
                                            className="flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs transition-colors hover:bg-secondary text-foreground/70"
                                        >
                                            <ChartBar size={12} weight="duotone" />
                                            <span className="truncate">{r.name}</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {visibleNavItems.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            data-testid={it.testid}
                            title={collapsed ? it.label : undefined}
                            className={({ isActive }) =>
                                `flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2 rounded-sm text-sm transition-colors ${
                                    isActive
                                        ? "bg-foreground text-background"
                                        : "hover:bg-secondary text-foreground/80"
                                }`
                            }
                        >
                            <it.icon size={18} weight="duotone" />
                            {!collapsed && <span>{it.label}</span>}
                        </NavLink>
                    ))}
                </nav>
                <div className={`${collapsed ? "p-2" : "p-3"} border-t border-border`}>
                    {!collapsed && (
                        <div className="px-3 py-2 mb-2">
                            <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                            <div className="label-overline mt-1">{user?.role?.replace("_", " ")}</div>
                        </div>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        className={`w-full ${collapsed ? "px-0 justify-center" : "justify-start"} rounded-sm`}
                        onClick={handleLogout}
                        data-testid="logout-button"
                        title={collapsed ? "Sign out" : undefined}
                    >
                        <SignOut size={16} className={collapsed ? "" : "mr-2"} />
                        {!collapsed && <span>Sign out</span>}
                    </Button>
                </div>
            </aside>

            <main className="flex-1 overflow-auto relative">
                <WideModeToggle />
                <Outlet />
            </main>
        </div>
    );
}

function WideModeToggle() {
    const { wide, setWide } = useLayoutPrefs();
    return (
        <button
            type="button"
            onClick={() => setWide(!wide)}
            className="absolute top-3 right-3 z-30 flex items-center gap-1 border border-border bg-card/90 hover:bg-secondary backdrop-blur rounded-sm px-2 py-1 text-[10px] font-mono uppercase tracking-wider transition-colors"
            data-testid="wide-mode-toggle"
            aria-pressed={wide}
            title={wide ? "Switch to comfortable width" : "Use full screen width"}
        >
            {wide ? <ArrowsIn size={11} /> : <ArrowsOut size={11} />}
            <span className="hidden sm:inline">{wide ? "Comfort" : "Full width"}</span>
        </button>
    );
}
